import { useMemo, useState } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { SeoHead } from "@/components/seo/SeoHead";
import { Section } from "@/components/marketing/Section";
import { Eyebrow } from "@/components/marketing/Eyebrow";
import { DisplayHeading } from "@/components/marketing/DisplayHeading";
import { Rule } from "@/components/marketing/Rule";
import { ArticleCard } from "@/components/marketing/ArticleCard";

import { buildBreadcrumbJsonLd, buildOrganizationJsonLd } from "@/lib/seo";
import { useMarketingArticles } from "@/hooks/useMarketingArticles";
import { RESOURCE_CATEGORIES, isResourceCategorySlug } from "@/lib/marketing-categories";
import { cn } from "@/lib/utils";
import { useLocale } from "@/lib/locale";

const PAGE_SIZE = 12;

export default function ResourcesCategoryPage() {
  // Same fix as ResourceArticlePage.tsx: the matched route is
  // /ressources/:slugOrCategory (ResourcesParamDispatcher decides between the
  // two pages), so the route param key is slugOrCategory, not category --
  // reading `category` here always returned undefined, which made every
  // single category page redirect to the hub for every real visitor.
  const { slugOrCategory: category } = useParams<{ slugOrCategory: string }>();
  const { t, i18n } = useTranslation();
  const uiLocale = useLocale();
  const [page, setPage] = useState(1);

  const isValidCategory = !!category && isResourceCategorySlug(category);
  const categorySlug = isValidCategory ? category : "";
  const cat = isValidCategory
    ? RESOURCE_CATEGORIES.find((c) => c.slug === categorySlug)
    : undefined;

  const { data: articles = [] } = useMarketingArticles(categorySlug);

  const totalPages = useMemo(
    () => Math.max(1, Math.ceil(articles.length / PAGE_SIZE)),
    [articles.length],
  );

  const slice = articles.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  if (!isValidCategory || !cat) {
    return <Navigate to="/ressources" replace />;
  }

  const label = t(`marketing.resources.categories.${cat.i18n}.label`);

  return (
    <>
      <SeoHead
        title={t(`marketing.resources.categories.${cat.i18n}.seoTitle`)}
        description={t(`marketing.resources.categories.${cat.i18n}.seoDescription`)}
        path={`/ressources/${category}`}
        locale={uiLocale}
        jsonLd={[
          buildOrganizationJsonLd(),
          buildBreadcrumbJsonLd([
            { name: t("marketing.breadcrumbs.home"), path: "/" },
            { name: t("marketing.header.nav.resources"), path: "/ressources" },
            { name: label, path: `/ressources/${category}` },
          ]),
        ]}
      />

      <Section padding="lg">
        <div className="max-w-3xl">
          <Eyebrow>{t("marketing.resources.category.eyebrow")}</Eyebrow>
          <DisplayHeading level={1} className="mt-4">{label}</DisplayHeading>
          <p className="mt-6 text-lg leading-relaxed text-muted-foreground md:text-xl">
            {t(`marketing.resources.categories.${cat.i18n}.intro`)}
          </p>
        </div>

        <div className="mt-8 flex flex-wrap gap-2">
          <Link
            to="/ressources"
            className="inline-flex h-9 items-center border border-rule px-3 text-xs font-medium uppercase tracking-wider text-muted-foreground hover:border-foreground hover:text-foreground"
          >
            {t("marketing.resources.hub.all")}
          </Link>
          {RESOURCE_CATEGORIES.map((c) => (
            <Link
              key={c.slug}
              to={`/ressources/${c.slug}`}
              className={cn(
                "inline-flex h-9 items-center border px-3 text-xs font-medium uppercase tracking-wider",
                c.slug === category ? "border-accent-press bg-accent-press text-accent-press-foreground" : "border-rule text-muted-foreground hover:border-foreground hover:text-foreground",
              )}
            >
              {t(`marketing.resources.categories.${c.i18n}.label`)}
            </Link>
          ))}
        </div>
      </Section>

      <Rule />

      <Section padding="md">
        <div className="grid grid-cols-1 gap-px bg-rule md:grid-cols-2 lg:grid-cols-3">
          {slice.map((a) => (
            <ArticleCard key={a.id} article={a} />
          ))}
        </div>
        {articles.length === 0 && (
          <p className="mt-10 text-center text-sm text-muted-foreground">{t("marketing.resources.hub.empty")}</p>
        )}
        {totalPages > 1 && (
          <nav className="mt-12 flex items-center justify-center gap-2">
            {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
              <button
                key={p}
                onClick={() => setPage(p)}
                className={cn(
                  "h-9 min-w-[36px] border px-3 text-sm",
                  p === page ? "border-foreground bg-foreground text-background" : "border-rule text-muted-foreground hover:border-foreground hover:text-foreground",
                )}
              >
                {p}
              </button>
            ))}
          </nav>
        )}
      </Section>
    </>
  );
}
